import type { Query } from "@tanstack/react-query";
import { QueryClientProvider } from "@tanstack/react-query";
import { useSnackbar } from "notistack";
import { useLayoutEffect } from "react";
import { useNavigate } from "react-router";

import { RouteWatcher } from "@citrine/atoms/route-watcher";
import {
  SnackbarProvider,
  getPermissionSnackbarOptions,
} from "@citrine/atoms/snackbar";
import { isAccessError } from "@citrine/client";
import { queryClient, subscribe } from "@citrine/client/utils";
import type { LangProps } from "@citrine/lang";
import { LangProvider } from "@citrine/lang";
import type { ThemeProps } from "@citrine/theme";
import { ThemeProvider } from "@citrine/theme";

import { CatalystProvider } from "../catalyst";
import { TokenProvider } from "../token";

import "./error-logging";

function scrollToTop() {
  window.scrollTo(0, 0);
}

function ScrollToTopOnNavigation() {
  return <RouteWatcher onNavigation={scrollToTop} />;
}

function AccessErrorHandler() {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  useLayoutEffect(() => {
    const handleError = (error: unknown, query: Query) => {
      if (isAccessError(error)) {
        navigate("/", { replace: true });
        enqueueSnackbar(
          getPermissionSnackbarOptions({ key: `auth:${query.queryKey}` })
        );
      }
    };

    // handle errors that occurred prior to effect firing
    queryClient
      .getQueryCache()
      .getAll()
      .filter((query) => query.state.error)
      .forEach((query) => handleError(query.state.error, query));

    return subscribe("error", handleError);
  }, [enqueueSnackbar, navigate]);

  return null;
}

export function RootContext({
  children,
  timeZone,
}: LangProps & ThemeProps & { timeZone?: string }) {
  return (
    <ThemeProvider>
      <QueryClientProvider client={queryClient}>
        <LangProvider timeZone={timeZone}>
          <SnackbarProvider>
            <AccessErrorHandler />
            <CatalystProvider>
              <ScrollToTopOnNavigation />
              <TokenProvider>{children}</TokenProvider>
            </CatalystProvider>
          </SnackbarProvider>
        </LangProvider>
      </QueryClientProvider>
    </ThemeProvider>
  );
}
